import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, renderSlot as _renderSlot, createBlock as _createBlock, normalizeClass as _normalizeClass, withCtx as _withCtx, mergeProps as _mergeProps, renderList as _renderList, createSlots as _createSlots } from "vue"

const _hoisted_1 = { class: "flex gap-xs items-center justify-between q-px-md q-mb-lg q-mt-md" }
const _hoisted_2 = { class: "col-grow fw-600 fs-17px text-cool-gray-100 k-dialog__title" }
const _hoisted_3 = {
  key: 1,
  class: "flex items-center gap-md"
}
const _hoisted_4 = { class: "BaseTableDialog" }
const _hoisted_5 = { class: "flex items-center justify-between q-mb-20" }
const _hoisted_6 = {
  key: 1,
  class: "q-mb-none text-cool-gray-100 fs-17px fw-600"
}
const _hoisted_7 = { class: "flex items-center justify-between" }
const _hoisted_8 = {
  key: 0,
  class: "fs-15px fw-500 text-info-100"
}

import _ from 'lodash'
import { computed, reactive, watch, watchEffect } from 'vue'
import translate from 'src/common/helpers/language/translate'
import globalTranslateObject from 'src/translate/global.translate'
import { IBaseTableDialogOptions, IDropdownOption, IResponse, ITableColumn, ITableRow } from 'src/common/interfaces'
import { useAxios } from 'src/services/__vue_query__/api/fetchByApiClient'
import { handleAxiosResponse } from 'src/services/__vue_query__/api/helpers'
import { catchError } from 'src/common/helpers/generator/error.helper'
import useNotify from 'src/hook/useNotify'

interface IProps {
  title?: string
  options: IBaseTableDialogOptions
  selectedIds?: number[]
}

interface IEmits {
  (event: 'select', data: ITableRow | ITableRow[]): void
}

interface IPagination {
  page: number
  rowsPerPage: number
  totalCount: number
}

interface IState {
  options: IBaseTableDialogOptions
  data: {
    list: ITableRow[]
  }
  loading: boolean
  filter: {
    search?: string
    page: number
    row_number: number
    [key: string]: unknown
  }
  pagination: IPagination
  tableCols: ITableColumn[]
  tableRows: ITableRow[]
  multiple: boolean
  selectedRows: ITableRow[]
  dropdown: {
    options: IDropdownOption[] | { [key: string]: unknown }[]
    value: null | string | number
  }
  searchToggle: boolean
  [key: string]: unknown
}

type PaginatedListResponse = IResponse<{ list: ITableRow[]; count: number }>


export default /*@__PURE__*/_defineComponent({
  ...{ name: 'BaseTableDialog', inheritAttrs: false },
  __name: 'BaseTableDialog',
  props: /*@__PURE__*/_mergeModels({
    title: { default: 'Select Item' },
    options: {},
    selectedIds: { default: () => [] }
  }, {
    "modelValue": { type: Boolean, required: true },
    "modelModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["select"], ["update:modelValue"]),
  setup(__props: any, { emit: __emit }) {


const model = _useModel(__props, "modelValue")
const props = __props
const emit = __emit

/* -------------------------- Translation Variables ------------------------- */

const { t: globalT } = translate(globalTranslateObject)

/* -------------------------------- Constants ------------------------------- */

const { notify } = useNotify()

const state = reactive<IState>({
  options: props.options as IBaseTableDialogOptions,
  data: {
    list: [],
  },
  loading: false,
  filter: { search: '', page: 1, row_number: 100 },
  pagination: {
    page: 1,
    rowsPerPage: 100,
    totalCount: 0,
  },
  tableCols: [],
  tableRows: [],
  multiple: false,
  selectedRows: [],
  dropdown: {
    options: [],
    value: null,
  },
  searchToggle: false,
})

state.filter = { ...state.filter, ...state.options.filter }
const pagesNumber = computed(() => Math.ceil(state.pagination.totalCount / state.pagination.rowsPerPage))

state.tableCols = state.options.columns
const perPageOptions = [
  { label: '5', value: 5 },
  { label: '10', value: 10 },
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
  { label: '200', value: 200 },
]

/* -------------------------------- Functions ------------------------------- */

async function getData() {
  state.loading = true
  const filter = { ...state.filter }
  if (state.options.searchKey) {
    filter[state.options.searchKey] = filter.search
    filter.search = undefined
  }
  const request = useAxios<PaginatedListResponse>(state.options.endpoint, { filter })
  try {
    await request.fetch()
    const response = handleAxiosResponse<PaginatedListResponse>(request)
    state.data = response.data
    if (state.multiple && state.options.rowKey && state.options.rowKey !== 'id') {
      state.data.list = state.data.list.map((data) => {
        return { ...data, id: data[state.options.rowKey!] }
      })
    }
    state.pagination.totalCount = response.data.count
    if (props.selectedIds.length)
      state.selectedRows = state.data.list.filter((row) => props.selectedIds.includes(Number(row.id)))
  } catch (err) {
    const errors = catchError(err)
    for (const error of errors) notify(false, error)
  } finally {
    state.loading = false
  }
}

async function getDropdownData() {
  state.loading = true
  const request = useAxios<PaginatedListResponse>(state.options.dropdown.endpoint, {
    filter: state.options.dropdown.filter,
  })
  try {
    await request.fetch()
    const response = handleAxiosResponse<PaginatedListResponse>(request)
    state.dropdown.options = response.data.list.map((data) => {
      const dataItem = { ...data }
      for (const key of Object.keys(state.options.dropdown.bind)) {
        const value = state.options.dropdown.bind[key]
        if (typeof value === 'boolean') dataItem[key] = value
        else dataItem[key] = dataItem[value as string]
      }
      return dataItem
    })
    const allItems = {
      label: state.options?.dropdown?.allLabel ?? 'All',
      value: -1,
      status: state.options?.dropdown?.bind?.status === true,
    }
    state.dropdown.options.unshift(allItems)
    if (state.dropdown.value === null) state.dropdown.value = -1
  } catch (err) {
    const errors = catchError(err)
    for (const error of errors) notify(false, error)
  } finally {
    state.loading = false
  }
}

function selectRow(row: ITableRow) {
  if (!state.multiple) emit('select', row)
}

function onPageChanged() {
  state.filter.page = state.pagination.page
  getData()
}

function onPerPageChanged() {
  state.filter.row_number = state.pagination.rowsPerPage
  state.filter.page = state.pagination.page = 1
  getData()
}

function toggleSearch() {
  state.searchToggle = !state.searchToggle
}

function resetState() {
  state.selectedRows = []
  state.filter.search = ''
  state.filter.page = state.pagination.page = 1
  state.filter.row_number = state.pagination.rowsPerPage = 100
  state.searchToggle = false
  if (state.options.dropdown?.payloadProperty) _.omit(state.filter, state.options.dropdown.payloadProperty)
}

/* -------------------------------- Watchers -------------------------------- */

watchEffect(() => {
  state.options = props.options
  state.filter = { ...state.filter, ...state.options.filter }
  state.multiple = Boolean(state.tableCols.find((col) => col.name === 'selection'))
})

watch(model, () => {
  if (model.value) {
    getData()
    if (state.options.dropdown?.endpoint) getDropdownData()
  } else resetState()
})

watch(
  () => state.dropdown.value,
  async (value, oldValue) => {
    if (value && state.options.dropdown?.payloadProperty && oldValue !== null) {
      state.filter[state.options.dropdown.payloadProperty] = value === -1 ? undefined : value
      if (value === -1) {
        state.pagination.rowsPerPage = state.pagination.totalCount <= 100 ? 100 : 200
        onPerPageChanged()
      } else await getData()
    }
  },
  { deep: true },
)

watch(
  () => state.filter.search,
  () => {
    state.pagination.page = 1
    state.filter.page = 1
    getData()
  },
  { deep: true },
)

return (_ctx: any,_cache: any) => {
  const _component_k_icon = _resolveComponent("k-icon")!
  const _component_k_btn = _resolveComponent("k-btn")!
  const _component_k_dropdown = _resolveComponent("k-dropdown")!
  const _component_k_input = _resolveComponent("k-input")!
  const _component_EmptyListAlert = _resolveComponent("EmptyListAlert")!
  const _component_BaseLandingList = _resolveComponent("BaseLandingList")!
  const _component_k_divider = _resolveComponent("k-divider")!
  const _component_k_pagination = _resolveComponent("k-pagination")!
  const _component_k_dialog = _resolveComponent("k-dialog")!

  return (_openBlock(), _createBlock(_component_k_dialog, {
    modelValue: model.value,
    "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((model).value = $event)),
    title: __props.title,
    class: "BaseTableDialogWrapper"
  }, {
    header: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, _toDisplayString(__props.title), 1 /* TEXT */),
        (!state.multiple)
          ? (_openBlock(), _createElementBlock("span", {
              key: 0,
              class: "rounded-circle q-pa-xs bg-white flex inline cursor-pointer",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (model.value = false))
            }, [
              _createVNode(_component_k_icon, {
                name: "close-rounded",
                size: "24px",
                color: "cool-gray-70"
              })
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createVNode(_component_k_btn, {
                class: "BaseTableDialog__header-btn",
                model: "secondary",
                label: _unref(globalT)('cancel'),
                size: "md",
                onClick: _cache[1] || (_cache[1] = ($event: any) => (model.value = false))
              }, null, 8 /* PROPS */, ["label"]),
              _createVNode(_component_k_btn, {
                class: "BaseTableDialog__header-btn",
                model: "primary",
                label: _unref(globalT)('add'),
                size: "md",
                onClick: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('select', state.selectedRows)))
              }, null, 8 /* PROPS */, ["label"])
            ]))
      ])
    ]),
    content: _withCtx(() => [
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _renderSlot(_ctx.$slots, "title", {}, () => [
            (state.options.dropdown)
              ? (_openBlock(), _createBlock(_component_k_dropdown, {
                  key: 0,
                  modelValue: state.dropdown.value,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((state.dropdown.value) = $event)),
                  options: state.dropdown.options,
                  placeholder: state.options.dropdown.placeholder,
                  class: "BaseTableDialog__dropdown full-width",
                  "root-class": "flex-1"
                }, null, 8 /* PROPS */, ["modelValue", "options", "placeholder"]))
              : (state.options.title)
                ? (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(state.options.title), 1 /* TEXT */))
                : _createCommentVNode("v-if", true)
          ]),
          _createVNode(_component_k_input, {
            modelValue: state.filter.search,
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((state.filter.search) = $event)),
            class: _normalizeClass(["BaseTableDialog__search", {
              'BaseTableDialog__search--inactive': !state.searchToggle,
            }]),
            "root-class": "q-ml-auto",
            icon: "search",
            placeholder: "Search",
            debounce: "1000"
          }, {
            prepend: _withCtx(() => [
              _createVNode(_component_k_icon, {
                size: "24px",
                name: "search",
                color: "cool-gray-70",
                class: "cursor-pointer",
                onClick: toggleSearch
              })
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["modelValue", "class"])
        ]),
        _createVNode(_component_BaseLandingList, _mergeProps({
          modelValue: state.selectedRows,
          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((state.selectedRows) = $event)),
          class: "BaseTableDialog__table",
          columns: state.tableCols,
          rows: state.data.list,
          loading: state.loading,
          "header-root-class": "hidden",
          "table-class": "hide-scrollbar",
          "has-footer": false,
          "allow-row-selection": state.multiple,
          "allow-row-deselection": false,
          "preselected-ids": __props.selectedIds
        }, _ctx.$attrs, {
          onRowClicked: _cache[6] || (_cache[6] = ($event: any) => (selectRow($event)))
        }), _createSlots({
          "no-data": _withCtx(() => [
            (!state.loading)
              ? (_openBlock(), _createBlock(_component_EmptyListAlert, {
                  key: 0,
                  illustration: "/images/illustration.png",
                  title: (state.options.noData && state.options.noData.title) || 'No data was found!',
                  description: (state.options.noData && state.options.noData.description) || ''
                }, null, 8 /* PROPS */, ["title", "description"]))
              : _createCommentVNode("v-if", true)
          ]),
          _: 2 /* DYNAMIC */
        }, [
          _renderList(state.tableCols, (column) => {
            return {
              name: column.slot,
              fn: _withCtx(({ col, localProps }) => [
                _renderSlot(_ctx.$slots, column.slot, {
                  col: col,
                  localProps: localProps
                })
              ])
            }
          })
        ]), 1040 /* FULL_PROPS, DYNAMIC_SLOTS */, ["modelValue", "columns", "rows", "loading", "allow-row-selection", "preselected-ids"]),
        _createElementVNode("div", null, [
          _createVNode(_component_k_divider, {
            size: "md",
            class: "q-mb-md"
          }),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("div", null, [
              (state.multiple && state.selectedRows.length)
                ? (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(state.selectedRows.length) + " " + _toDisplayString(state.selectedRows.length === 1 ? 'Item has' : 'Items have') + " been selected ", 1 /* TEXT */))
                : _createCommentVNode("v-if", true)
            ]),
            _createVNode(_component_k_pagination, {
              "per-page": state.pagination.rowsPerPage,
              "onUpdate:perPage": [
                _cache[7] || (_cache[7] = ($event: any) => ((state.pagination.rowsPerPage) = $event)),
                _cache[10] || (_cache[10] = ($event: any) => (onPerPageChanged()))
              ],
              modelValue: state.pagination.page,
              "onUpdate:modelValue": [
                _cache[8] || (_cache[8] = ($event: any) => ((state.pagination.page) = $event)),
                _cache[9] || (_cache[9] = ($event: any) => (onPageChanged()))
              ],
              max: pagesNumber.value,
              total: state.pagination.totalCount,
              disable: state.loading,
              "per-page-options": perPageOptions,
              "root-class": "table-pagination"
            }, null, 8 /* PROPS */, ["per-page", "modelValue", "max", "total", "disable"])
          ])
        ])
      ])
    ]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["modelValue", "title"]))
}
}

})